import getInteractiveChildren from '@/js/utils/getInteractiveChildren';
import getFirstAndLastItems from '@/js/utils/getFirstAndLastItems';

/**
 * ModalDialog component.
 */
export default class ModalDialog {
  /**
   * Get the target element based on the controller's `aria-controls` attribute.
   *
   * @param  {HTMLButtonElement} controller The component's controlling element.
   * @return {HTMLElement|null}
   */
  static getTargetElement(controller) {
    if (!controller.hasAttribute('aria-controls')) {
      const message = 'The component element is missing the required \'aria-controls\' attribute';
      throw new Error(`Configuration error: ${message}`);
    }

    const targetId = controller.getAttribute('aria-controls');
    const target = document.getElementById(targetId);

    if (target === null) {
      const message = `A target element with ID of '${targetId}' is not found`;
      throw new Error(`Configuration error: ${message}`);
    }

    return target;
  }

  /**
   * Start the component
   */
  constructor(controller, options = {}) {
    // Collect elements.
    this.controller = controller;
    this.target = this.constructor.getTargetElement(controller);

    // Bind class methods.
    this.init = this.init.bind(this);
    this.setState = this.setState.bind(this);
    this.stateWasUpdated = this.stateWasUpdated.bind(this);
    this.hide = this.hide.bind(this);
    this.show = this.show.bind(this);
    this.handleTargetKeydown = this.handleTargetKeydown.bind(this);
    this.handleCloseButtonClick = this.handleCloseButtonClick.bind(this);
    this.handleTargetShown = this.handleTargetShown.bind(this);
    this.hideOnOutsideClick = this.hideOnOutsideClick.bind(this);
    this.destroy = this.destroy.bind(this);

    /**
     * Component configuration options.
     *
     * @type {object}
     */
    const defaultOptions = {
      /**
       * Whether the CSS uses a transition, which will impact the ability to
       * focus elements within the target element.
       *
       * @type {boolean}
       */
      useTransition: false,

      /**
       * The selector for the target's close button.
       *
       * @type {string}
       */
      close: '',

      /**
       * The classname to add to the target when it is shown.
       *
       * @type {string}
       */
      shownClass: 'menu-button__target--is-shown',
    };

    // Merge options with defaults.
    this.options = Object.assign(defaultOptions, options);

    const { close } = this.options;
    if (close !== '') {
      /**
       * The element within the ModalDialog target used to hide the target.
       *
       * @type {HTMLButtonElement}
       */
      this.closeButton = this.target.querySelector(close);
    }

    /**
     * Component state.
     *
     * @type {object}
     */
    this.state = {
      expanded: false,
    };

    this.init();
  }

  /**
   * Set up the component's DOM attributes and event listeners.
   */
  init() {
    // Collect the target element's interactive child elements.
    const interactiveChildElements = getInteractiveChildren(this.target);

    /*
     * Collect first and last interactive child elements from target and merge
     * them in as instance properties.
     */
    if (interactiveChildElements.length > 0) {
      const [
        firstInteractiveChild,
        lastInteractiveChild,
      ] = getFirstAndLastItems(interactiveChildElements);

      Object.assign(this, { firstInteractiveChild, lastInteractiveChild });
    }

    /*
     * Set the taget as hidden by default. Using the `aria-hidden` attribute,
     * rather than the `hidden` attribute, means authors must hide the target
     * element via CSS.
     */
    this.target.setAttribute('aria-hidden', 'true');
    this.target.setAttribute('role', 'dialog');
    this.target.setAttribute('aria-modal', 'true');

    // Add event listeners
    this.controller.addEventListener('click', this.show);
    this.target.addEventListener('keydown', this.handleTargetKeydown);
    document.body.addEventListener('click', this.hideOnOutsideClick);

    if (this.closeButton != null) {
      this.closeButton.addEventListener('click', this.handleCloseButtonClick);
    }

    const { useTransition } = this.options;
    if (useTransition) {
      this.target.addEventListener('transitionend', this.handleTargetShown);
    }
  }

  /**
   * Set component state.
   *
   * @param {object} newState The new state to merge with existing state.
   */
  setState(newState) {
    const updatedState = { ...this.state, ...newState };
    this.state = updatedState;

    this.stateWasUpdated(updatedState);
  }

  /**
   * Update the component attributes based on updated state.
   *
   * @param {object} state The component state.
   */
  stateWasUpdated({ expanded }) {
    const { useTransition, shownClass } = this.options;
    this.controller.setAttribute('aria-expanded', `${expanded}`);

    // Update target attributes and activeElement.
    if (expanded) {
      this.target.setAttribute('aria-hidden', 'false');
      this.target.classList.add(shownClass);

      if (!useTransition) {
        this.firstInteractiveChild.focus();
      }
    } else {
      this.target.setAttribute('aria-hidden', 'true');
      this.target.classList.remove(shownClass);
    }
  }

  /**
   * Handle keydown events on the target.
   *
   * @param {Event} event The event object.
   */
  handleTargetKeydown(event) {
    const { code, shiftKey } = event;
    const { expanded } = this.state;
    const { activeElement } = document;

    if (code === 'Escape' && expanded) {
      event.preventDefault();

      // Hide the target when the Escape key is pressed.
      this.hide();

      // Move focus to the controller.
      this.controller.focus();
    } else if (code === 'Tab') {
      // Trap tabs within the modal.
      if (shiftKey) {
        if (this.firstInteractiveChild === activeElement) {
          event.preventDefault();
          this.lastInteractiveChild.focus();
        }
      } else if (this.lastInteractiveChild === activeElement) {
        event.preventDefault();
        this.firstInteractiveChild.focus();
      }
    }
  }

  /**
   * Handle click events on the close button.
   *
   * @param {Event} event The event object.
   */
  handleCloseButtonClick(event) {
    const { expanded } = this.state;

    if (expanded) {
      event.preventDefault();

      this.hide();
      this.controller.focus();
    }
  }

  /**
   * Handle visible target after a CSS transition.
   */
  handleTargetShown(event) {
    // Bail early if the transition is on a child element.
    if (this.target !== event.target) {
      return;
    }

    this.firstInteractiveChild.focus();
  }

  /**
   * Close the target when clicking anywhere outside of the target or controller
   * elements.
   *
   * @param {Event} event The event object.
   */
  hideOnOutsideClick(event) {
    const { expanded } = this.state;
    const { target: clicked } = event;

    if (
      expanded
      && clicked !== this.controller
      && !this.target.contains(clicked)
    ) {
      this.hide();
    }
  }

  /**
   * Update component state to show the target element.
   */
  show() {
    this.setState({ expanded: true });
  }

  /**
   * Update component state to hide the target element.
   */
  hide() {
    this.setState({ expanded: false });
  }

  /**
   * Remove all attributes and event listeners added by this class.
   */
  destroy() {
    const { useTransition, shownClass } = this.options;

    // Remove controller attributes.
    this.controller.removeAttribute('aria-expanded');

    // Remove target attributes.
    this.target.removeAttribute('aria-hidden');
    this.target.removeAttribute('role');
    this.target.removeAttribute('aria-modal');
    this.target.classList.remove(shownClass);

    // Remove event listeners.
    this.controller.removeEventListener('click', this.show);
    this.target.removeEventListener('keydown', this.handleTargetKeydown);

    if (this.closeButton != null) {
      this.closeButton.removeEventListener('click', this.handleCloseButtonClick);
    }

    if (useTransition) {
      this.target.removeEventListener('transitionend', this.handleTargetShown);
    }

    // Reset initial state.
    this.state = {
      expanded: false,
    };
  }
}
